import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { saasRequest } from './api';
import { useDispatch } from 'react-redux';
import { getUser } from '../features/userSlice/userSlice';
import { setTenantBusinessInfo } from '../pages/Shared/BusinessInfo';
import { applyTenantTheme } from './themes';

const SessionContext = createContext(null);

const allLegacyPermissions = [
  'userManagement', 'productsInfo', 'purchase', 'managePurchase', 'sales', 'manageSales', 'accountsInfo', 'transactions', 'reports', 'salesReturn', 'showCostInInvoice', 'cashStatement', 'bankStatement', 'balanceSheet', 'profitSheet', 'profitSheetAdvance', 'currentStock', 'stockLedger', 'customerDueReport', 'customerLedgerReport', 'supplierDueReport', 'supplierLedgerReport', 'loanDueReport', 'loanLedgerReport', 'invoiceWiseProfit',
  'addBankAccount', 'addBankDeposit', 'addBankWithdraw', 'addBrand', 'addCategory', 'addCollection', 'addCustomer', 'addExpense', 'addExpenseHead', 'addIncome', 'addIncomeHead', 'addLoanAccount', 'addLoanReturn', 'addLoanTaken', 'addOpeningCapital', 'addOrigin', 'addPayment', 'addProduct', 'addPurchase', 'addSales', 'addSupplier', 'addUnit',
  'deleteBankAccount', 'deleteBankDeposit', 'deleteBankWithdraw', 'deleteBrand', 'deleteCategory', 'deleteCollection', 'deleteCustomer', 'deleteExpense', 'deleteExpenseHead', 'deleteIncome', 'deleteIncomeHead', 'deleteLoanAccount', 'deleteLoanReturn', 'deleteLoanTaken', 'deleteOpeningCapital', 'deleteOrigin', 'deletePayment', 'deleteProduct', 'deletePurchase', 'deleteSales', 'deleteSupplier', 'deleteUnit',
  'editBankAccount', 'editBankDeposit', 'editBankWithdraw', 'editBrand', 'editCategory', 'editCollection', 'editCustomer', 'editExpense', 'editExpenseHead', 'editIncome', 'editIncomeHead', 'editLoanAccount', 'editLoanReturn', 'editLoanTaken', 'editOpeningCapital', 'editOrigin', 'editPayment', 'editProduct', 'editPurchase', 'editSales', 'editSupplier', 'editUnit', 'approvalBrand', 'approvalCategory',
];

function legacyPermissionsFor(user, membership) {
  if (!user || !membership) return [];
  if (membership.permissions && typeof membership.permissions === 'object') {
    return { email: user.email, name: user.name, type: membership.role, ...membership.permissions };
  }
  const permissions = Object.fromEntries(allLegacyPermissions.map((permission) => [permission, false]));
  if (membership.role === 'owner') Object.assign(permissions, Object.fromEntries(allLegacyPermissions.map((permission) => [permission, true])));
  if (membership.role === 'manager') {
    Object.assign(permissions, Object.fromEntries(allLegacyPermissions.filter((permission) => !['userManagement'].includes(permission)).map((permission) => [permission, true])));
  }
  if (membership.role === 'cashier') Object.assign(permissions, { sales: true, addSales: true, manageSales: true, addCustomer: true, editCustomer: true });
  if (membership.role === 'salesman') Object.assign(permissions, { sales: true, addSales: true, manageSales: true, addCustomer: true });
  return { email: user.email, name: user.name, type: membership.role, ...permissions };
}

export function SaasSessionProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null, tenant: null, membership: null });
  const dispatch = useDispatch();

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('posSaasSession');
    if (!token) {
      setTenantBusinessInfo();
      applyTenantTheme();
      setState({ loading: false, user: null, tenant: null, membership: null });
      return null;
    }
    try {
      const data = await saasRequest('/auth/me');
      let settings = null;
      if (data.tenant) settings = await saasRequest('/tenant/settings');
      const localizedSettings = settings ? { ...settings, Bangla_Language: settings.language === 'bn' } : settings;
      const enriched = { ...data, tenant: data.tenant ? { ...data.tenant, settings: localizedSettings } : null };
      setTenantBusinessInfo(localizedSettings || {});
      applyTenantTheme(settings?.themePreset);
      setState({ loading: false, ...enriched });
      return enriched;
    } catch (_error) {
      localStorage.removeItem('posSaasSession');
      setTenantBusinessInfo();
      applyTenantTheme();
      setState({ loading: false, user: null, tenant: null, membership: null });
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { dispatch(getUser(legacyPermissionsFor(state.user, state.membership))); }, [dispatch, state.user, state.membership]);

  const login = async (email, password) => {
    const data = await saasRequest('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
    localStorage.setItem('posSaasSession', data.token);
    await refresh();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('posSaasSession');
    dispatch(getUser([]));
    setTenantBusinessInfo();
    applyTenantTheme();
    setState({ loading: false, user: null, tenant: null, membership: null });
  };

  return <SessionContext.Provider value={{ ...state, refresh, login, logout }}>{children}</SessionContext.Provider>;
}

export function useSaasSession() {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSaasSession must be used inside SaasSessionProvider.');
  return context;
}
